import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
// Public pages — eagerly loaded because they're the landing + auth entry
// points and first paint matters.
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Authenticated + admin pages — lazy-loaded so the initial JS bundle
// doesn't include them. Shaves ~500kB off the landing payload.
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateVisualization = lazy(() => import("./pages/CreateVisualization"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const Library = lazy(() => import("./pages/Library"));
const Profile = lazy(() => import("./pages/Profile"));
const Progress = lazy(() => import("./pages/Progress"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminTemplates = lazy(() => import("./pages/admin/AdminTemplates"));
const AdminKnowledge = lazy(() => import("./pages/admin/AdminKnowledge"));
const AdminQuestionnaires = lazy(() => import("./pages/admin/AdminQuestionnaires"));
const AdminSports = lazy(() => import("./pages/admin/AdminSports"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const SandboxLandingPageB = lazy(() => import("./pages/sandbox/SandboxLandingPageB"));

import { Loader2 } from "lucide-react";

const queryClient = new QueryClient();

const FullScreenLoader = ({ message }: { message: string }) => (
  <div className="min-h-screen gradient-hero flex flex-col items-center justify-center gap-4 px-6 text-center">
    <div className="font-display text-3xl font-bold text-gradient-primary">VIZ</div>
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader message="Loading your session..." />;
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const SubscribedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, subscription, onboardingCompleted } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  if (loading || subscription.loading || adminLoading || onboardingCompleted === null) {
    return <FullScreenLoader message="Setting things up..." />;
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!onboardingCompleted && !isAdmin) return <Navigate to="/onboarding" replace />;
  if (!subscription.subscribed && !isAdmin) return <Navigate to="/subscribe" replace />;
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  if (authLoading || adminLoading) {
    return <FullScreenLoader message="Checking access..." />;
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader message="Loading..." />;
  }

  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<FullScreenLoader message="Loading…" />}>
            <Routes>
              <Route path="/" element={<SandboxLandingPageB />} />
              <Route path="/sandbox/landing-page-a" element={<Index />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
              <Route path="/dashboard" element={<SubscribedRoute><Dashboard /></SubscribedRoute>} />
              <Route path="/library" element={<SubscribedRoute><Library /></SubscribedRoute>} />
              <Route path="/analytics" element={<SubscribedRoute><Progress /></SubscribedRoute>} />
              <Route path="/profile" element={<SubscribedRoute><Profile /></SubscribedRoute>} />
              <Route path="/create/:type" element={<SubscribedRoute><CreateVisualization /></SubscribedRoute>} />
              <Route path="/admin/templates" element={<AdminRoute><AdminTemplates /></AdminRoute>} />
              <Route path="/admin/knowledge" element={<AdminRoute><AdminKnowledge /></AdminRoute>} />
              <Route path="/admin/questionnaires" element={<AdminRoute><AdminQuestionnaires /></AdminRoute>} />
              <Route path="/admin/sports" element={<AdminRoute><AdminSports /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/sandbox/landing-page-b" element={<SandboxLandingPageB />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
